/**
 * Bani → Verificare cifre (03.11.2026). Cifrele din CRM fata de sursa lor de adevar, pe o luna:
 * verde / galben / rosu / gri, cu regula si documentele care nu se potrivesc. Doar citire.
 */
import { Fragment, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ChevronDown, ChevronRight, Play } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { AntetPagina } from '../../components/ui/AntetPagina'
import { Button } from '../../components/ui/Button'
import { CardCifra } from '../../components/ui/CardCifra'
import { Pastila } from '../../components/ui/Pastila'
import type { Ton } from '../../components/ui/TonuriB4L'

type Culoare = 'verde' | 'galben' | 'rosu' | 'gri'

interface Verificare {
  cod: string
  titlu: string
  regula: string
  surse: string
  crm: number | null
  referinta: number | null
  diferenta: number | null
  toleranta: string
  culoare: Culoare
  unitate?: 'lei' | 'ore' | 'documente'
  nota?: string
  detalii?: { coloane: string[]; randuri: (string | number | null)[][] }
}

interface Rulare {
  id: number
  luna: string
  declansator: string
  pornitLa: string
  terminatLa: string | null
  stare: 'in_curs' | 'ok' | 'eroare'
  rezumat: Record<Culoare, number> | null
  eroare: string | null
  rezultate?: Verificare[] | null
}

const CULOARE: Record<Culoare, { ton: Ton; eticheta: string }> = {
  verde: { ton: 'verde', eticheta: 'se potrivește' },
  galben: { ton: 'galben', eticheta: 'de explicat' },
  rosu: { ton: 'rosu', eticheta: 'de investigat' },
  gri: { ton: 'gri', eticheta: 'fără date' },
}

const fmt = (n: number | null) =>
  n == null
    ? '—'
    : n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Lei cu doua zecimale; orele si documentele ca numere intregi, cu unitatea lor. */
function cifra(n: number | null, unitate: Verificare['unitate']): string {
  if (n == null) return '—'
  if (unitate === 'ore') return `${Math.round(n)} h`
  if (unitate === 'documente') return String(n)
  return fmt(n)
}

function lunaTrecuta(): string {
  const [a, l] = new Date()
    .toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' })
    .split('-')
    .map(Number) as [number, number]
  return l === 1 ? `${a - 1}-12` : `${a}-${String(l - 1).padStart(2, '0')}`
}

export function VerificareCifrePage() {
  const qc = useQueryClient()
  const poateRula = useAuthStore((s) => s.permissions.includes('verificareCifre:write'))
  const [luna, setLuna] = useState(lunaTrecuta())
  const [alesId, setAlesId] = useState<number | null>(null)
  const [deschis, setDeschis] = useState<string | null>(null)

  const lista = useQuery<{ rulari: Rulare[]; inCurs: boolean }>({
    queryKey: ['verificare-cifre'],
    queryFn: async () => (await api.get('/verificare-cifre')).data,
  })
  const idAfisat = alesId ?? lista.data?.rulari[0]?.id ?? null
  const detaliu = useQuery<{ rulare: Rulare }>({
    queryKey: ['verificare-cifre', idAfisat],
    queryFn: async () => (await api.get(`/verificare-cifre/${idAfisat}`)).data,
    enabled: idAfisat != null,
  })

  const ruleaza = useMutation({
    mutationFn: async () => (await api.post('/verificare-cifre/ruleaza', { luna })).data,
    onSuccess: (d: { rulare: Rulare }) => {
      setAlesId(d.rulare.id)
      void qc.invalidateQueries({ queryKey: ['verificare-cifre'] })
      toast.success(`Verificarea pe ${d.rulare.luna} s-a terminat.`)
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error(e.response?.data?.message ?? 'Verificarea a eșuat.'),
  })

  const r = detaliu.data?.rulare
  const vs = r?.rezultate ?? []

  return (
    <div className="space-y-4">
      <AntetPagina
        titlu="Verificare cifre"
        descriere="Cifrele din CRM față de contabilitate, e-Factura, bancă și eMAG, pe o lună."
        explicatie="Doar citește copiile din CRM; nu schimbă nimic. Verde = egal (cel mult rotunjirea), galben = diferență sub 1% sau de știut, roșu = peste 1%, gri = fără date."
        actiuni={
          poateRula ? (
            <div className="flex items-center gap-2">
              <input
                type="month"
                value={luna}
                onChange={(e) => setLuna(e.target.value)}
                className="rounded-md border border-b4l-border bg-b4l-bg px-2 py-1 text-sm text-b4l-text"
                aria-label="Luna verificată"
              />
              <Button onClick={() => ruleaza.mutate()} disabled={ruleaza.isPending || !luna}>
                <Play className="h-4 w-4" /> {ruleaza.isPending ? 'Rulează…' : 'Rulează acum'}
              </Button>
            </div>
          ) : undefined
        }
      />

      {!!lista.data?.rulari.length && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-b4l-text-muted">Rulări:</span>
          <select
            value={idAfisat ?? ''}
            onChange={(e) => setAlesId(Number(e.target.value))}
            className="rounded-md border border-b4l-border bg-b4l-bg px-2 py-1 text-b4l-text"
          >
            {lista.data.rulari.map((x) => (
              <option key={x.id} value={x.id}>
                {x.luna} · {new Date(x.pornitLa).toLocaleString('ro-RO')} · {x.declansator}
                {x.stare === 'eroare' ? ' · eșuată' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {lista.data && !lista.data.rulari.length && (
        <p className="py-8 text-center text-sm text-b4l-text-muted">
          Nicio verificare încă. Alege luna și apasă „Rulează acum”.
        </p>
      )}

      {r?.stare === 'eroare' && (
        <p className="rounded-lg bg-b4l-red-light p-3 text-sm text-b4l-red-dark">
          Rularea a eșuat: {r.eroare}
        </p>
      )}

      {r?.rezumat && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(['verde', 'galben', 'rosu', 'gri'] as Culoare[]).map((c) => (
            <CardCifra
              key={c}
              ton={CULOARE[c].ton}
              eticheta={CULOARE[c].eticheta}
              valoare={r.rezumat?.[c] ?? 0}
              detaliu={`luna ${r.luna}`}
            />
          ))}
        </div>
      )}

      {!!vs.length && (
        <div className="overflow-x-auto rounded-lg border border-b4l-border">
          <table className="w-full text-sm">
            <thead className="bg-b4l-bg-soft text-left text-xs text-b4l-text-muted">
              <tr>
                <th className="px-3 py-2">Verificare</th>
                <th className="px-3 py-2 text-right">CRM</th>
                <th className="px-3 py-2 text-right">Sursa</th>
                <th className="px-3 py-2 text-right">Diferență</th>
                <th className="px-3 py-2">Toleranță</th>
                <th className="px-3 py-2">Stare</th>
              </tr>
            </thead>
            <tbody>
              {vs.map((v) => {
                const e = deschis === v.cod
                return (
                  <Fragment key={v.cod}>
                    <tr
                      className="cursor-pointer border-t border-b4l-border hover:bg-b4l-bg-soft"
                      onClick={() => setDeschis(e ? null : v.cod)}
                    >
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          aria-expanded={e}
                          onClick={(ev) => {
                            ev.stopPropagation()
                            setDeschis(e ? null : v.cod)
                          }}
                          className="inline-flex items-center gap-1 text-left"
                        >
                          {e ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          {v.titlu}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {cifra(v.crm, v.unitate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {cifra(v.referinta, v.unitate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {cifra(v.diferenta, v.unitate)}
                      </td>
                      <td className="px-3 py-2 text-xs text-b4l-text-muted">{v.toleranta}</td>
                      <td className="px-3 py-2">
                        <Pastila ton={CULOARE[v.culoare].ton}>
                          {CULOARE[v.culoare].eticheta}
                        </Pastila>
                      </td>
                    </tr>
                    {e && (
                      <tr className="bg-b4l-bg-soft">
                        <td colSpan={6} className="space-y-2 px-3 py-3 text-xs">
                          <p>
                            <strong>Regula:</strong> {v.regula}
                          </p>
                          <p>
                            <strong>Surse:</strong> {v.surse}
                          </p>
                          {v.nota && <p>{v.nota}</p>}
                          {v.detalii && (
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr>
                                    {v.detalii.coloane.map((c) => (
                                      <th key={c} className="px-2 py-1 text-left">
                                        {c}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {v.detalii.randuri.map((rand, i) => (
                                    <tr key={i} className="border-t border-b4l-border">
                                      {rand.map((x, j) => (
                                        <td key={j} className="px-2 py-1">
                                          {typeof x === 'number' ? fmt(x) : (x ?? '—')}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
